import ArticleIcon from '@mui/icons-material/Article';
import ImageIcon from '@mui/icons-material/Image';
import RemoveCircleOutlineOutlinedIcon from '@mui/icons-material/RemoveCircleOutlineOutlined';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { Avatar, Box, Stack, Typography } from '@mui/material';
import { useEffect, useMemo } from 'react';
import useDragAndDrop from '/@/hooks/drag-and-drop/useDragAndDrop';
import { _ } from '/@/locale';
import Alert from '../../bayer/alert';

const getExtension = (file) => `.${file.name.split('.').pop()}`;

const formatSizeKb = (bytes: number) => `${Math.round(bytes / 1024).toLocaleString()} KB`;

const requiredExtensionsToString = (requiredExtensions) =>
    '(' + requiredExtensions.map((el) => el.join(', ')).join(_(') o (')) + ')';

// requiredExtensions = [[this AND this AND this] OR [this AND this ...] ...]
function filterValidFiles(files, requiredExtensions = []) {
    const errors = [];
    const extensionsMatched = requiredExtensions.map((eGroup) =>
        eGroup.reduce((pv, cv) => {
            pv[cv] = false;
            return pv;
        }, {})
    );

    // Check each file against the required extensions
    for (const file of files) {
        const extension = getExtension(file);
        extensionsMatched.forEach((extensionsGroup) => {
            if (extensionsGroup[extension] === false) {
                extensionsGroup[extension] = true;
            }
        });
    }

    // All files match at least one required extension group
    for (const extensionsGroup of extensionsMatched) {
        if (Object.values(extensionsGroup).every(Boolean))
            return files.filter((file) => {
                const extension = getExtension(file);
                return extensionsGroup[extension];
            });
    }
    return [];
}

const FILE_ICONS = {
    '.jpg': <ImageIcon color='primary' sx={{ fontSize: 20 }} />,
    '.jpeg': <ImageIcon color='primary' sx={{ fontSize: 20 }} />,
    '.png': <ImageIcon color='primary' sx={{ fontSize: 20 }} />,
    '.gif': <ImageIcon color='primary' sx={{ fontSize: 20 }} />,
    '.pdf': <ArticleIcon color='primary' sx={{ fontSize: 20 }} />,
} as const;

const FileDropperV2 = ({
    requiredExtensions = [],
    files,
    setFiles,
    onValidFiles = () => {},
    text,
    disabled = false,
    maxFiles,
}: {
    requiredExtensions: string[][];
    files: File[];
    setFiles: React.Dispatch<File[]>;
    onValidFiles?: (validFiles: File[]) => void;
    text: string;
    disabled?: boolean;
    /** When set, caps how many files can be attached; excess attempts show an error. */
    maxFiles?: number;
}) => {
    const { dragOver, setDragOver, onDragOver, onDragLeave, fileDropError, setFileDropError } = useDragAndDrop();

    const acceptAttr = useMemo(() => requiredExtensions.map((el) => el.join(',')).join(','), [requiredExtensions]);

    const validateFiles = (selectedFiles) => {
        const errors = [];
        try {
            const loadedFilesExtensions = {};
            let name;

            // Check loaded files
            for (const file of files) {
                if (!name) name = file.name.slice(0, file.name.length - 4);
                const extention = getExtension(file);
                loadedFilesExtensions[extention] = true;
            }

            const validNewFiles: File[] = [];

            // Check new files
            for (const file of Object.values(selectedFiles) as File[]) {
                const extention = getExtension(file);
                if (!name) name = file.name.slice(0, file.name.length - 4);
                // Check invalid extension
                if (!requiredExtensions.some((extensionsGroup) => extensionsGroup.includes(extention))) {
                    errors.push(
                        _(
                            'Archivo: {{file}}: extension incorrecta, el archivo debe poseer extension: {{extentions}}.',
                            {
                                extentions: requiredExtensionsToString(requiredExtensions),
                                file: file.name,
                            }
                        )
                    );
                    continue;
                }

                validNewFiles.push(file);
            }

            if (maxFiles !== undefined && validNewFiles.length > 0) {
                const remaining = maxFiles - files.length;
                if (remaining <= 0) {
                    errors.push(_('No podés adjuntar más de {{count}} archivo', { count: maxFiles }));
                    validNewFiles.length = 0;
                } else if (validNewFiles.length > remaining) {
                    errors.push(_('No podés adjuntar más de {{count}} archivo', { count: maxFiles }));
                    validNewFiles.length = remaining;
                }
            }

            if (validNewFiles.length > 0) {
                setFiles((prev) => [...prev, ...validNewFiles]);
            }

            setFileDropError(errors);
        } catch (error) {
            setFileDropError([_('Error inesperado...')]);
        }
    };

    const onDrop = (e) => {
        e.preventDefault();

        setDragOver(false);

        const selectedFiles = e.dataTransfer.files;
        validateFiles(selectedFiles);
    };

    const onFilesSelected = (e) => {
        const selectedFiles = e.target.files;

        validateFiles(selectedFiles);
    };

    const onDelete = (file) => {
        setFileDropError([]);
        setFiles((files) => files.filter((el) => el.name !== file.name));
    };

    const validFiles = useMemo(() => {
        return filterValidFiles(files, requiredExtensions);
    }, [files, requiredExtensions]);
    useEffect(() => onValidFiles(validFiles), [validFiles]);

    return (
        <Stack
            direction='column'
            spacing={2}
            sx={{ width: '100%' }}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
            disabled={disabled}
        >
            {files?.map((file) => (
                <Stack
                    key={file.name}
                    direction='row'
                    alignItems='center'
                    justifyContent='space-between'
                    spacing={1}
                    sx={{
                        border: '1px solid',
                        borderColor: (t) => t.palette.divider,
                        p: 2,
                    }}
                >
                    <Stack direction='row' alignItems='center' spacing={1}>
                        <Avatar
                            sx={{
                                backgroundColor: (t) => t.palette.primary.infoBackground,
                                width: 32,
                                height: 32,
                                p: 2,
                            }}
                        >
                            {FILE_ICONS[getExtension(file) as keyof typeof FILE_ICONS]}
                        </Avatar>
                        <Stack direction='column' alignItems='start'>
                            <Typography variant='body1' color='primary' fontWeight='bold'>
                                {file.name}
                            </Typography>
                            <Typography variant='body1' color='primary.mui.inactive'>
                                {formatSizeKb(file.size)}
                            </Typography>
                        </Stack>
                    </Stack>
                    <RemoveCircleOutlineOutlinedIcon
                        color='secondary'
                        sx={{ fontSize: 20, cursor: 'pointer' }}
                        onClick={() => onDelete(file)}
                    />
                </Stack>
            ))}
            <Box
                component='label'
                sx={{
                    border: '1px dashed',
                    borderColor: 'primary',
                    p: 2,
                    width: '100%',
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    display: 'block',
                    ...(disabled ? { pointerEvents: 'none', opacity: 0.6 } : {}),
                }}
            >
                <input
                    type='file'
                    name='file'
                    multiple
                    hidden
                    accept={acceptAttr}
                    onChange={onFilesSelected}
                    disabled={disabled}
                />
                <Stack direction='row' alignItems='center' justifyContent='center' spacing={1} width='100%'>
                    <UploadFileIcon sx={{ fontSize: 20, color: (t) => t.palette.primary.onSurface }} />
                    <Typography variant='body1' color='primary.mui.inactive' component='span'>
                        {text}
                    </Typography>
                </Stack>
            </Box>

            {fileDropError.map((err) => (
                <Box key={err}>
                    <Alert severity='danger'>{err}</Alert>
                </Box>
            ))}
        </Stack>
    );
};

export default FileDropperV2;
